import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { r2Storage } from '../services/r2Storage';
import { supabase } from '../lib/supabase';
import { trackCheckoutCompleted } from '../services/ga4Analytics';

const OnboardingTrialSuccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false); // Prevent multiple verifications

  useEffect(() => {
    // Only run verification once
    if (!isVerifying) {
      verifyAndCompleteOnboarding();
    }
  }, []);

  const verifyAndCompleteOnboarding = async () => {
    if (isVerifying) {
      console.log('⚠️ Verification already in progress, skipping...');
      return;
    }
    
    setIsVerifying(true);
    try {
      // CRITICAL: Get session_id from Stripe redirect
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        console.error('❌ No session_id found - possible payment bypass attempt');
        setError('Invalid payment session. Please complete payment setup.');
        setIsLoading(false);
        return;
      }

      console.log('✅ Verifying Stripe session:', sessionId);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required to verify payment session');
      }
      
      // SECURITY: Verify the session with our backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const verifyResponse = await fetch(`${apiUrl}/api/stripe/verify-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          sessionId
        })
      });

      if (!verifyResponse.ok) {
        throw new Error('Payment verification failed');
      }

      const verification = await verifyResponse.json();
      
      if (!verification.success) {
        throw new Error(verification.message || 'Payment not completed');
      }

      console.log('✅ Payment verified, now processing logo upload and completing onboarding');
      
      // 🚀 LOGO UPLOAD: Handle pending logo upload from sessionStorage
      let logoUrl = null;
      try {
        const pendingLogoData = sessionStorage.getItem('pendingLogoUpload');
        if (pendingLogoData) {
          console.log('📤 Found pending logo upload, processing...');
          const logoData = JSON.parse(pendingLogoData);
          
          // Convert base64 back to File object
          const response = await fetch(logoData.fileData);
          const blob = await response.blob();
          const file = new File([blob], logoData.fileName, { type: logoData.fileType });
          
          // Upload to Cloudflare R2
          const uploadResult = await r2Storage.uploadLogo(file);
          logoUrl = uploadResult.url;
          
          // Clean up sessionStorage
          sessionStorage.removeItem('pendingLogoUpload');
          
          console.log('✅ Logo uploaded successfully:', logoUrl);
          toast.success('Logo uploaded successfully!', {
            toastId: 'logo-upload-success'
          });
        }
      } catch (logoError) {
        console.error('⚠️ Logo upload failed (continuing anyway):', logoError);
        toast.warn('Logo upload failed, but your account setup is complete. You can upload your logo later in Profile Settings.', {
          toastId: 'logo-upload-warning'
        });
      }
      
      // ONLY mark as completed AFTER payment verification (and optional logo upload)
      const profileUpdates = {
        onboarding_completed: true,
        trial_setup_completed: true,
        onboarding_step: 'completed',
        stripe_customer_id: verification.customerId,
        subscription_id: verification.subscriptionId
      };
      
      // Add logo URL if upload was successful
      if (logoUrl) {
        profileUpdates.avatar_url = logoUrl;
      }
      
      await authService.updateProfile(profileUpdates);
      trackCheckoutCompleted('pro', verification.subscriptionId || sessionId);
      
      // Wait a moment for database to update, then redirect
      console.log('✅ Profile updated, redirecting to dashboard...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
      
      setIsLoading(false);
      toast.success('Welcome to QuoteTier! Your free trial has started.', {
        toastId: 'trial-welcome'
      });
      
    } catch (error) {
      console.error('❌ Onboarding verification error:', error);
      setError(error.message || 'Failed to verify payment. Please contact support.');
      setIsLoading(false);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRetryPayment = () => {
    navigate('/onboarding/trial-setup');
  };

  const handleCreateFirstQuote = () => {
    console.log('🚀 Navigating to quote builder...');
    navigate('/quote-builder');
  };

  const handleGoToDashboard = () => {
    console.log('📦 Navigating to dashboard...');
    navigate('/dashboard');
  };

  // SECURITY: Show error if payment verification failed
  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 rounded-full mb-6">
            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-4 font-instrument">Payment Verification Failed</h1>
          <p className="text-muted-foreground font-jetbrains mb-6">{error}</p>
          <div className="space-y-4">
            <button
              onClick={handleRetryPayment}
              className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-instrument"
            >
              Complete Payment Setup
            </button>
            <p className="text-xs text-gray-500 font-jetbrains">
              Need help? Contact support@QuoteTier.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="spinner spinner-lg mx-auto mb-4"></div>
          <p className="font-jetbrains text-lg text-gray-600">Setting up your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-3 text-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            <span className="font-instrument text-3xl">QuoteTier</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-instrument text-4xl sm:text-5xl tracking-tight leading-tight mb-4">
              <span className="text-primary font-bold">Welcome aboard!</span>
            </h1>
            <p className="font-jetbrains text-xl text-gray-600 mb-8">
              Your Pro account is active. Time to create quotes that actually convert.
            </p>
          </div>

          {/* Trial Info */}
          <div className="border border-black rounded-lg p-8 bg-white mb-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="font-instrument text-3xl font-bold text-green-600 mb-2">14</div>
                <p className="font-jetbrains text-sm text-gray-600">Days of free access</p>
              </div>
              <div>
                <div className="font-instrument text-3xl font-bold text-blue-600 mb-2">∞</div>
                <p className="font-jetbrains text-sm text-gray-600">Unlimited quotes</p>
              </div>
              <div>
                <div className="font-instrument text-3xl font-bold text-purple-600 mb-2">$0</div>
                <p className="font-jetbrains text-sm text-gray-600">Charged until trial ends</p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="border border-gray-200 rounded-lg p-8 bg-gray-50 mb-8">
            <h2 className="font-instrument text-2xl mb-6">Ready to get started?</h2>
            <div className="space-y-4">
              <div className="flex items-start text-left">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-1 flex-shrink-0">1</div>
                <div>
                  <h3 className="font-instrument text-lg font-medium">Create your first quote</h3>
                  <p className="font-jetbrains text-gray-600">Start with our Good/Better/Best template and see the interactive magic</p>
                </div>
              </div>
              <div className="flex items-start text-left">
                <div className="w-8 h-8 bg-gray-400 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-1 flex-shrink-0">2</div>
                <div>
                  <h3 className="font-instrument text-lg font-medium">Send to a test client</h3>
                  <p className="font-jetbrains text-gray-600">Experience how your customers will interact with your quotes</p>
                </div>
              </div>
              <div className="flex items-start text-left">
                <div className="w-8 h-8 bg-gray-400 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-1 flex-shrink-0">3</div>
                <div>
                  <h3 className="font-instrument text-lg font-medium">Watch your conversion rates soar</h3>
                  <p className="font-jetbrains text-gray-600">Track views, engagement, and signatures in your dashboard</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={handleCreateFirstQuote}
              className="font-instrument text-xl w-full sm:w-auto px-8 py-4 bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg transition-colors"
            >
              Create Your First Quote
            </button>
            
            <div className="text-center">
              <button
                onClick={handleGoToDashboard}
                className="font-jetbrains text-gray-600 hover:text-gray-800 underline"
              >
                Or explore your dashboard first
              </button>
            </div>
          </div>

          {/* Support Info */}
          <div className="mt-12 p-6 border border-gray-200 rounded-lg bg-blue-50">
            <h3 className="font-instrument text-lg font-medium mb-2">Need help getting started?</h3>
            <p className="font-jetbrains text-sm text-gray-600 mb-4">
              Our team is here to help you succeed. Reach out anytime during your trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:support@QuoteTier.com" 
                className="font-jetbrains text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Email Support
              </a>
              <span className="hidden sm:block text-gray-400">•</span>
              <a 
                href="#" 
                className="font-jetbrains text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Help Center
              </a>
              <span className="hidden sm:block text-gray-400">•</span>
              <a 
                href="#" 
                className="font-jetbrains text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Video Tutorials
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingTrialSuccess;
